import React from "react";
import { FeatherInfo } from "@subframe/core";
import {
  RECEIVABLES_AGING_SCOPE_TOOLTIP,
  useReceivablesAging,
} from "../../hooks/useReceivablesAging";
import { ReceivablesAgingSectionSkeleton } from "../shared/ReceivablesAgingSectionSkeleton";

export interface AgingBucket {
  color: string;
  label: string;
  amount: string;
  percent: string;
  barHeight?: number;
}

export interface ReceivablesAgingSectionProps {
  title?: string;
  totalOutstanding?: string;
  buckets?: AgingBucket[];
  alertMessage?: string;
}

export function ReceivablesAgingSection(props?: ReceivablesAgingSectionProps) {
  const data = useReceivablesAging();

  const title = data.title;
  const asOfSubtitle = data.asOfSubtitle;
  const totalOutstanding = data.totalOutstanding;
  const overdueAmount = data.overdueAmount;
  const notOverdueAmount = data.notOverdueAmount;
  const buckets = data.buckets;
  const loading = "loading" in data ? data.loading : false;
  const error = "error" in data ? data.error : null;

  if (loading) {
    return <ReceivablesAgingSectionSkeleton />;
  }

  if (error) {
    return (
      <div className="flex h-full min-h-0 w-full min-w-0 flex-col items-start gap-6 rounded-lg border border-solid border-neutral-200 bg-white px-6 py-6 shadow-sm">
        <div className="flex w-full flex-col items-start gap-1">
          <span className="text-heading-3 font-heading-3 text-neutral-900">
            Receivables Aging
          </span>
        </div>
        <p className="text-body font-body text-neutral-500">{error}</p>
      </div>
    );
  }

  const MAX_BAR_HEIGHT = 260;
  const MIN_BAR_HEIGHT = 2;

  function parseCompactAmount(input: string): number {
    const raw = input
      .replace(/outstanding|overdue|not overdue/gi, "")
      .replace(/[₹$€£,\s]/g, "")
      .trim();
    if (!raw) return 0;

    const m = raw.match(/^(-?\d+(?:\.\d+)?)([a-zA-Z]+)?$/);
    if (!m) return 0;

    const value = Number(m[1]);
    if (!Number.isFinite(value)) return 0;

    const suffix = (m[2] ?? "").toLowerCase();
    const mult =
      suffix === "k"
        ? 1_000
        : suffix === "l"
          ? 100_000
          : suffix === "cr"
            ? 10_000_000
            : 1;
    return value * mult;
  }

  // UI request: show "Not overdue" as an additional bucket in the chart.
  // `notOverdueAmount` is already displayed in the subtitle; this is purely a visual bucket.
  const notOverdueBucket: AgingBucket = {
    color: "#a3a3a3",
    label: "Not overdue",
    amount: (notOverdueAmount || "—")
      .replace(/\s+not\s+overdue\s*$/i, "")
      .trim(),
    percent: "",
    barHeight: 0,
  };

  const baseBuckets = [notOverdueBucket, ...(buckets ?? [])];
  const totalOutstandingValue = parseCompactAmount(totalOutstanding || "");
  const maxValue = Math.max(
    0,
    ...baseBuckets.map((b) => parseCompactAmount(b.amount)),
  );
  const chartBuckets = baseBuckets.map((b) => {
    const v = parseCompactAmount(b.amount);
    const pct =
      totalOutstandingValue > 0 ? (v / totalOutstandingValue) * 100 : 0;
    let barHeight = 0;
    if (maxValue > 0 && v > 0) {
      // Non-linear scaling so small buckets visibly differ even when one bucket dominates.
      // sqrt keeps ordering but increases separation near zero.
      const scaled = Math.sqrt(v / maxValue);
      barHeight = Math.round(scaled * (MAX_BAR_HEIGHT - MIN_BAR_HEIGHT) + MIN_BAR_HEIGHT);
      if (barHeight < MIN_BAR_HEIGHT) barHeight = MIN_BAR_HEIGHT;
      if (barHeight > MAX_BAR_HEIGHT) barHeight = MAX_BAR_HEIGHT;
    }
    return {
      ...b,
      percent: `${pct.toFixed(1)}%`,
      barHeight,
    };
  });

  return (
    <div className="flex h-full min-h-0 w-full min-w-0 flex-col items-start gap-6 rounded-lg border border-solid border-neutral-200 bg-white px-6 py-6 shadow-sm">
      <div className="flex w-full shrink-0 flex-col items-start gap-1">
        <span className="text-heading-3 font-heading-3 text-neutral-900">
          {title}
        </span>
        {asOfSubtitle ? (
          <div className="flex items-center gap-1.5">
            <span className="text-caption font-caption text-neutral-500">
              {asOfSubtitle}
            </span>
            <button
              type="button"
              className="inline-flex shrink-0 rounded p-0.5 text-neutral-400 hover:text-neutral-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand-600"
              title={RECEIVABLES_AGING_SCOPE_TOOLTIP}
              aria-label={RECEIVABLES_AGING_SCOPE_TOOLTIP}
            >
              <FeatherInfo className="h-4 w-4" />
            </button>
          </div>
        ) : null}
        <span className="text-caption font-caption text-neutral-500">
          {totalOutstanding}
        </span>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="text-caption font-caption text-neutral-500">
            {notOverdueAmount} and {overdueAmount}
          </span>
          {/* <span className="text-caption font-caption text-neutral-500">
            {overdueAmount}
          </span> */}
        </div>
      </div>
      <div className="flex min-h-0 w-full flex-1 flex-col justify-end gap-6 pb-6">
        <div className="flex w-full flex-col items-start gap-0.5 pt-2">
          <div className="flex h-64 min-h-[16rem] w-full flex-none items-end pt-6 md:h-72 md:min-h-[18rem]">
            {chartBuckets.map((b, i) => (
              <div
                key={i}
                className="flex flex-1 flex-col items-center justify-end px-3"
              >
                <div className="relative flex flex-col items-center">
                  <div
                    className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-[calc(100%+6px)]"
                    style={{
                      backgroundColor:
                        b.color === "#22c55e"
                          ? "#f0fdf4"
                          : b.color === "#3b82f6"
                            ? "#eff6ff"
                            : b.color === "#d97706"
                              ? "#fffbeb"
                              : "#fff1f2",
                    }}
                  >
                    <div
                      className="flex items-center rounded-full px-2 py-0.5"
                    >
                    <span
                      className="whitespace-nowrap font-['Inter'] text-[12px] font-[700] leading-[18px]"
                      style={{
                        color:
                          b.color === "#22c55e"
                            ? "#15803d"
                            : b.color === "#3b82f6"
                              ? "#1d4ed8"
                              : b.color === "#d97706"
                                ? "#b45309"
                                : "#b91c1c",
                      }}
                    >
                      {b.amount}
                    </span>
                  </div>
                </div>
                  <div
                    className="flex h-2.5 w-2.5 flex-none flex-col items-start rounded-full mt-[2px]"
                    style={{ backgroundColor: b.color }}
                  />
                  <div
                    className="flex w-0.5 flex-none flex-col items-start opacity-40 mt-[2px]"
                    style={{
                      height: b.barHeight ?? 40,
                      backgroundColor: b.color,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
          <div className="flex w-full items-start gap-4 mt-3">
            {chartBuckets.map((b, i) => (
              <div
                key={i}
                className="flex flex-col items-start gap-1 overflow-hidden flex-1"
              >
                <div
                  className="flex h-1 w-full flex-none items-start rounded-full"
                  style={{ backgroundColor: b.color }}
                />
                <span className="text-body-bold font-body-bold text-default-font mt-2">
                  {b.label}
                </span>
                <span className="text-caption font-caption text-neutral-500">
                  {b.percent}
                </span>
              </div>
            ))}
          </div>
        </div>
        {/* {alertMessage ? (
          <div className="flex w-full items-start gap-3 rounded-lg border border-solid border-error-200 bg-error-50 px-4 py-4">
            <FeatherAlertTriangle className="text-body font-body text-error-600 flex-none" />
            <span className="text-body-bold font-body-bold text-error-700">
              {alertMessage}
            </span>
          </div>
        ) : null} */}
      </div>
    </div>
  );
}
